import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { designerAPI, servicesAPI, portfolioAPI } from '../utils/api';
import { getAllThemes } from '../utils/themes';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, LinkSimple, Palette, Check, SignOut, ShareNetwork, Copy, WhatsappLogo, TrendUp, PencilSimple, Camera, UserCircle, InstagramLogo, FacebookLogo, YoutubeLogo, TwitterLogo, LinkedinLogo, Globe } from '@phosphor-icons/react';
import ImageUploader from '../components/ImageUploader';

export const ProfilePage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [editServiceDialogOpen, setEditServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const [profileForm, setProfileForm] = useState({
    name: '',
    mobile: '',
    location: '',
    bio: '',
    whatsapp: '',
    upi_id: '',
    photo_url: '',
    theme: 'modern_orange',
    // Social Media
    instagram: '',
    facebook: '',
    youtube: '',
    twitter: '',
    linkedin: '',
    website: '',
  });

  const themes = getAllThemes();

  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    unit: 'piece',
  });

  const [portfolioForm, setPortfolioForm] = useState({
    url: '',
    title: '',
    category: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, servicesRes, portfolioRes] = await Promise.all([
        designerAPI.getProfile(),
        servicesAPI.getAll(),
        portfolioAPI.getAll(),
      ]);
      setProfile(profileRes.data);
      setProfileForm({
        name: profileRes.data.name || '',
        mobile: profileRes.data.mobile || '',
        location: profileRes.data.location || '',
        bio: profileRes.data.bio || '',
        whatsapp: profileRes.data.whatsapp || '',
        upi_id: profileRes.data.upi_id || '',
        photo_url: profileRes.data.photo_url || '',
        theme: profileRes.data.theme || 'modern_orange',
        // Social Media
        instagram: profileRes.data.instagram || '',
        facebook: profileRes.data.facebook || '',
        youtube: profileRes.data.youtube || '',
        twitter: profileRes.data.twitter || '',
        linkedin: profileRes.data.linkedin || '',
        website: profileRes.data.website || '',
      });
      setServices(servicesRes.data);
      setPortfolio(portfolioRes.data);
    } catch (error) {
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await designerAPI.updateProfile(profileForm);
      toast.success('Profile updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleThemeChange = async (themeId) => {
    try {
      const updatedForm = { ...profileForm, theme: themeId };
      setProfileForm(updatedForm);
      await designerAPI.updateProfile(updatedForm);
      toast.success('Theme updated successfully!');
    } catch (error) {
      toast.error('Failed to update theme');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await servicesAPI.create({ ...serviceForm, price: parseFloat(serviceForm.price) });
      toast.success('Service added');
      setServiceDialogOpen(false);
      setServiceForm({ name: '', description: '', price: '', unit: 'piece' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const handleDeleteService = async (id) => {
    try {
      await servicesAPI.delete(id);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleEditService = (service) => {
    setEditingService({
      id: service.id,
      name: service.name,
      description: service.description || '',
      price: service.price,
      unit: service.unit || 'piece'
    });
    setEditServiceDialogOpen(true);
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      await servicesAPI.update(editingService.id, {
        name: editingService.name,
        description: editingService.description,
        price: parseFloat(editingService.price),
        unit: editingService.unit
      });
      toast.success('Service updated!');
      setEditServiceDialogOpen(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const handleAddPortfolio = async (e) => {
    e.preventDefault();
    try {
      await portfolioAPI.add(portfolioForm);
      toast.success('Portfolio item added');
      setPortfolioDialogOpen(false);
      setPortfolioForm({ url: '', title: '', category: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add portfolio item');
    }
  };

  const handleDeletePortfolio = async (id) => {
    try {
      await portfolioAPI.delete(id);
      toast.success('Portfolio item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete portfolio item');
    }
  };

  // Use short profile link for sharing (more professional)
  const publicProfileLink = profile ? `${window.location.origin}/${profile.profile_id || profile.id}` : '';

  const copyProfileLink = () => {
    navigator.clipboard.writeText(publicProfileLink);
    toast.success('Profile link copied!');
  };

  // Share functionality - uses native share on mobile, fallback to copy
  const shareProfileLink = async () => {
    const shareData = {
      title: `${profile?.name || 'Designer'} - Designbiz.in`,
      text: `${profile?.name || 'Designer'} चे Professional Portfolio बघा! Services, Pricing आणि Contact साठी या link वर click करा.`,
      url: publicProfileLink,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success('Link shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyProfileLink();
        }
      }
    } else {
      copyProfileLink();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
            Profile Setup
          </h1>
          <p className="text-[#6B7280] font-medium leading-relaxed">Manage your business profile</p>
        </div>

        <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-pink-50 mb-6" data-testid="public-profile-link-card">
          <CardContent className="pt-6">
            {/* Important Message */}
            <div className="mb-4 p-4 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl text-white">
              <div className="flex items-start gap-3">
                <TrendUp className="h-8 w-8 flex-shrink-0" weight="fill" />
                <div>
                  <h3 className="font-bold text-lg mb-1">तुमची Income वाढवा! 💰</h3>
                  <p className="text-sm text-white/90 leading-relaxed">
                    हे link <strong>WhatsApp, Instagram, Facebook</strong> वर share करा. 
                    Visiting Card वर print करा. जितके जास्त share कराल, तितके जास्त customers येतील!
                  </p>
                </div>
              </div>
            </div>

            <Label className="text-sm font-bold text-gray-700 mb-2 block">तुमचे Public Profile Link</Label>
            <div className="flex gap-2 mb-3">
              <Input 
                value={publicProfileLink} 
                readOnly 
                className="flex-1 bg-white border-2 border-orange-200 font-medium text-orange-600" 
                data-testid="public-profile-link-input" 
              />
              <Button 
                onClick={copyProfileLink} 
                data-testid="copy-profile-link-button" 
                className="bg-gray-800 hover:bg-gray-900 text-white"
                title="Copy Link"
              >
                <Copy className="h-4 w-4" weight="bold" />
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={shareProfileLink} 
                data-testid="share-profile-button" 
                className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold"
              >
                <ShareNetwork className="h-5 w-5 mr-2" weight="fill" />
                Share करा
              </Button>
              <Button 
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`माझे Professional Portfolio बघा: ${publicProfileLink}`)}`, '_blank')}
                className="bg-green-600 hover:bg-green-700 text-white"
                title="WhatsApp वर Share करा"
              >
                <WhatsappLogo className="h-5 w-5" weight="fill" />
              </Button>
            </div>

            <p className="text-xs text-gray-500 mt-3 text-center">
              💡 Tip: Bio मध्ये link टाका → Instagram, Facebook, YouTube, Twitter
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="profile-form-card">
          <CardHeader>
            <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              {/* Profile Photo - Prominent Round Display */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative">
                  {profileForm.photo_url ? (
                    <img 
                      src={profileForm.photo_url} 
                      alt="Profile" 
                      className="w-32 h-32 rounded-full object-cover border-4 border-orange-500 shadow-xl"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center border-4 border-orange-200 shadow-xl">
                      <UserCircle className="w-16 h-16 text-white" weight="duotone" />
                    </div>
                  )}
                  {/* Camera overlay for upload */}
                  <label className="absolute bottom-0 right-0 w-10 h-10 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors">
                    <Camera className="w-5 h-5 text-white" weight="fill" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('File size must be less than 5MB');
                          return;
                        }
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          toast.loading('Uploading...', { id: 'upload' });
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: formData
                          });
                          
                          if (!response.ok) throw new Error('Upload failed');
                          
                          const data = await response.json();
                          setProfileForm({ ...profileForm, photo_url: data.url });
                          toast.success('Photo uploaded!', { id: 'upload' });
                        } catch (error) {
                          toast.error('Upload failed', { id: 'upload' });
                        }
                      }}
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  {profileForm.photo_url ? 'Tap camera to change photo' : '📸 Profile Photo Upload करा'}
                </p>
                {!profileForm.photo_url && (
                  <p className="text-xs text-orange-600 font-medium mt-1">
                    Photo असलेल्या profiles ला 3x जास्त enquiry येतात!
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" data-testid="profile-name-input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input id="mobile" data-testid="profile-mobile-input" value={profileForm.mobile} onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input id="location" data-testid="profile-location-input" value={profileForm.location} onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea id="bio" data-testid="profile-bio-input" value={profileForm.bio} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} placeholder="Tell customers about your work..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp Number</Label>
                  <Input id="whatsapp" data-testid="profile-whatsapp-input" value={profileForm.whatsapp} onChange={(e) => setProfileForm({ ...profileForm, whatsapp: e.target.value })} placeholder="919876543210" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="upi_id">UPI ID</Label>
                  <Input id="upi_id" data-testid="profile-upi-input" value={profileForm.upi_id} onChange={(e) => setProfileForm({ ...profileForm, upi_id: e.target.value })} placeholder="yourname@upi" />
                </div>
              </div>

              {/* Social Media Links */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-bold text-gray-800 mb-3 block">🔗 Social Media Links</Label>
                <p className="text-xs text-gray-500 mb-3">तुमच्या social media links add करा - Public Profile वर दिसतील</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <InstagramLogo className="h-5 w-5 text-pink-600 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="instagram.com/username" 
                      value={profileForm.instagram} 
                      onChange={(e) => setProfileForm({ ...profileForm, instagram: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <FacebookLogo className="h-5 w-5 text-blue-600 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="facebook.com/username" 
                      value={profileForm.facebook} 
                      onChange={(e) => setProfileForm({ ...profileForm, facebook: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <YoutubeLogo className="h-5 w-5 text-red-600 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="youtube.com/@channel" 
                      value={profileForm.youtube} 
                      onChange={(e) => setProfileForm({ ...profileForm, youtube: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <TwitterLogo className="h-5 w-5 text-sky-500 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="twitter.com/username" 
                      value={profileForm.twitter} 
                      onChange={(e) => setProfileForm({ ...profileForm, twitter: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <LinkedinLogo className="h-5 w-5 text-blue-700 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="linkedin.com/in/username" 
                      value={profileForm.linkedin} 
                      onChange={(e) => setProfileForm({ ...profileForm, linkedin: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-gray-600 flex-shrink-0" weight="fill" />
                    <Input 
                      placeholder="yourwebsite.com" 
                      value={profileForm.website} 
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" data-testid="update-profile-button" className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">Update Profile</Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="services-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Services & Pricing</CardTitle>
            <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-service-button" size="sm" className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                  <Plus className="h-4 w-4 mr-1" weight="bold" /> Custom
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-service-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Service</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddService} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="service-name">Service Name</Label>
                    <Input id="service-name" data-testid="service-name-input" value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} placeholder="Logo Design" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-description">Description</Label>
                    <Textarea id="service-description" data-testid="service-description-input" value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} placeholder="Professional logo design..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="service-price">Price (₹)</Label>
                      <Input id="service-price" data-testid="service-price-input" type="number" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })} placeholder="5000" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service-unit">Unit</Label>
                      <Input id="service-unit" data-testid="service-unit-input" value={serviceForm.unit} onChange={(e) => setServiceForm({ ...serviceForm, unit: e.target.value })} placeholder="piece" />
                    </div>
                  </div>
                  <Button type="submit" data-testid="submit-service-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">Add Service</Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {/* Quick Add Service Suggestions */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">⚡ Quick Add - Tap करा:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Logo Design', price: 2500, unit: 'piece', desc: 'Professional logo design' },
                  { name: 'Visiting Card', price: 500, unit: '100 pcs', desc: 'Business card design & print' },
                  { name: 'Flex Banner', price: 25, unit: 'sq.ft', desc: 'Flex printing' },
                  { name: 'Pamphlet', price: 3, unit: 'piece', desc: 'Pamphlet design & print' },
                  { name: 'Wedding Card', price: 15, unit: 'piece', desc: 'Wedding invitation card' },
                  { name: 'Brochure', price: 2000, unit: 'design', desc: 'Brochure design' },
                  { name: 'Poster Design', price: 1000, unit: 'piece', desc: 'Poster/Banner design' },
                  { name: 'Letter Head', price: 800, unit: 'design', desc: 'Letterhead design' },
                  { name: 'Bill Book', price: 1500, unit: '50 pcs', desc: 'Invoice/Bill book print' },
                  { name: 'ID Card', price: 50, unit: 'piece', desc: 'ID card design & print' },
                  { name: 'Sticker', price: 10, unit: 'sq.inch', desc: 'Vinyl sticker printing' },
                  { name: 'Standee', price: 800, unit: 'piece', desc: 'Roll-up standee' },
                  { name: 'Glow Sign', price: 350, unit: 'sq.ft', desc: 'LED glow sign board' },
                  { name: 'ACP Board', price: 250, unit: 'sq.ft', desc: 'ACP sign board' },
                  { name: 'Sunboard', price: 45, unit: 'sq.ft', desc: 'Sunboard printing' },
                  { name: 'Menu Card', price: 100, unit: 'piece', desc: 'Restaurant menu design' },
                  { name: 'Social Media', price: 500, unit: 'post', desc: 'Social media post design' },
                  { name: 'Photo Editing', price: 200, unit: 'photo', desc: 'Photo retouching & editing' },
                ].filter(s => !services.some(existing => existing.name.toLowerCase() === s.name.toLowerCase()))
                 .map((suggestion) => (
                  <button
                    key={suggestion.name}
                    type="button"
                    onClick={async () => {
                      try {
                        await servicesAPI.create({
                          name: suggestion.name,
                          description: suggestion.desc,
                          price: suggestion.price,
                          unit: suggestion.unit
                        });
                        toast.success(`${suggestion.name} added!`);
                        const response = await servicesAPI.getAll();
                        setServices(response.data);
                      } catch (error) {
                        toast.error('Failed to add service');
                      }
                    }}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-orange-100 hover:text-orange-700 border border-gray-200 hover:border-orange-300 rounded-full text-sm font-medium text-gray-700 transition-all"
                  >
                    + {suggestion.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Services List */}
            <div className="space-y-3">
              {services.length === 0 ? (
                <p className="text-[#6B7280] text-sm text-center py-4">वरील buttons वर tap करून services add करा ☝️</p>
              ) : (
                services.map((service) => (
                  <div key={service.id} data-testid={`service-item-${service.id}`} className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg hover:border-orange-200 transition-colors">
                    <div className="flex-1" onClick={() => handleEditService(service)} style={{cursor: 'pointer'}}>
                      <h4 className="font-medium text-[#121212]">{service.name}</h4>
                      <p className="text-sm text-[#6B7280]">{service.description}</p>
                      <p className="text-sm font-medium text-[#FF5A36] mt-1">₹{service.price} / {service.unit}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditService(service)} title="Edit">
                        <PencilSimple className="h-4 w-4 text-blue-600" weight="duotone" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`delete-service-${service.id}`} onClick={() => handleDeleteService(service.id)} title="Delete">
                        <Trash className="h-4 w-4 text-red-600" weight="duotone" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Edit Service Dialog */}
            <Dialog open={editServiceDialogOpen} onOpenChange={setEditServiceDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Service</DialogTitle>
                </DialogHeader>
                {editingService && (
                  <form onSubmit={handleUpdateService} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-service-name">Service Name</Label>
                      <Input 
                        id="edit-service-name" 
                        value={editingService.name} 
                        onChange={(e) => setEditingService({...editingService, name: e.target.value})} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-service-description">Description</Label>
                      <Textarea 
                        id="edit-service-description" 
                        value={editingService.description} 
                        onChange={(e) => setEditingService({...editingService, description: e.target.value})} 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-service-price">Price (₹)</Label>
                        <Input 
                          id="edit-service-price" 
                          type="number" 
                          value={editingService.price} 
                          onChange={(e) => setEditingService({...editingService, price: e.target.value})} 
                          required 
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-service-unit">Unit</Label>
                        <Input 
                          id="edit-service-unit" 
                          value={editingService.unit} 
                          onChange={(e) => setEditingService({...editingService, unit: e.target.value})} 
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                      Save Changes
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB] bg-white" data-testid="portfolio-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Portfolio</CardTitle>
            <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-portfolio-button" size="sm" className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                  <Plus className="h-4 w-4 mr-1" weight="bold" /> Add Work
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-portfolio-dialog">
                <DialogHeader>
                  <DialogTitle>Add Portfolio Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPortfolio} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Portfolio Image</Label>
                    <ImageUploader
                      currentImage={portfolioForm.url}
                      onImageUploaded={(url) => setPortfolioForm({ ...portfolioForm, url: url })}
                      aspectRatio="landscape"
                      placeholder="Work Image Upload करा"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="portfolio-title">Title</Label>
                    <Input id="portfolio-title" data-testid="portfolio-title-input" value={portfolioForm.title} onChange={(e) => setPortfolioForm({ ...portfolioForm, title: e.target.value })} placeholder="Logo for ABC Company" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="portfolio-category">Category</Label>
                    <Input id="portfolio-category" data-testid="portfolio-category-input" value={portfolioForm.category} onChange={(e) => setPortfolioForm({ ...portfolioForm, category: e.target.value })} placeholder="Logo Design" />
                  </div>
                  <Button type="submit" data-testid="submit-portfolio-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full" disabled={!portfolioForm.url}>Add to Portfolio</Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="portfolio-grid">
              {portfolio.length === 0 ? (
                <p className="text-[#6B7280] text-sm">No portfolio items added yet</p>
              ) : (
                portfolio.map((item) => (
                  <div key={item.id} data-testid={`portfolio-item-${item.id}`} className="portfolio-item relative group">
                    <img src={item.url} alt={item.title} className="w-full h-64 object-cover rounded-lg" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <Button variant="destructive" size="sm" data-testid={`delete-portfolio-${item.id}`} onClick={() => handleDeletePortfolio(item.id)}>
                        <Trash className="h-4 w-4" weight="bold" />
                      </Button>
                    </div>
                    {item.title && <p className="mt-2 text-sm font-medium text-[#121212]">{item.title}</p>}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Theme Section - After Portfolio */}
        <Card className="border-[#E5E7EB] bg-white mb-6" data-testid="theme-selector-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="h-6 w-6 text-[#FF5A36]" weight="duotone" />
              <CardTitle className="font-['Chivo'] text-xl font-bold tracking-tight">Profile Theme</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#6B7280] mb-4">
              तुमच्या public profile साठी theme निवडा. तुमचे customers हे theme पाहतील.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {themes.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => handleThemeChange(theme.id)}
                  className={`relative p-4 border-2 rounded-lg transition-all hover:scale-105 ${
                    profileForm.theme === theme.id 
                      ? 'border-[#FF5A36] shadow-lg' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`theme-${theme.id}`}
                >
                  {profileForm.theme === theme.id && (
                    <div className="absolute top-2 right-2 bg-[#FF5A36] rounded-full p-1">
                      <Check className="h-4 w-4 text-white" weight="bold" />
                    </div>
                  )}
                  <div className="flex gap-2 mb-2">
                    <div 
                      className="w-8 h-8 rounded-full" 
                      style={{ background: `linear-gradient(135deg, ${theme.preview.primary}, ${theme.preview.secondary})` }}
                    ></div>
                    <div 
                      className="w-8 h-8 rounded-full" 
                      style={{ backgroundColor: theme.preview.accent }}
                    ></div>
                  </div>
                  <h4 className="font-bold text-[#121212] text-sm mb-1">{theme.name}</h4>
                  <p className="text-xs text-[#6B7280]">{theme.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Logout Section */}
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Logout</h3>
                <p className="text-sm text-gray-500">तुमच्या account मधून बाहेर पडा</p>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleLogout}
                data-testid="profile-logout-button"
                className="bg-red-600 hover:bg-red-700"
              >
                <SignOut className="h-5 w-5 mr-2" weight="bold" />
                Logout
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;